// Phase HR-003 Wave 3 — Grievance Inbox (HR / HOD / Plant Head view).
//
// Mobile-first; same hr-mobile-polish vocabulary as the other Phase HR
// pages. Each grievance is a card; SLA timer turns red when overdue.
//
// Actions (one-tap):
//   - Reply         — appends a note + attachment (no status change)
//   - Assign        — pick a user; status → IN_PROGRESS
//   - Escalate      — pick a user; status stays IN_PROGRESS
//   - Resolve       — closure_summary required
//   - Reject        — closure_summary required
//   - Close         — sets satisfaction_score, finalises

import { useMemo, useState } from 'react'
import {
  Alert, App, Button, Drawer, Empty, Input, List, Modal, Rate, Select,
  Space, Tag, Typography,
} from 'antd'
import {
  CheckCircleOutlined, HistoryOutlined, MessageOutlined, PlusOutlined,
  SendOutlined, StopOutlined, UserSwitchOutlined, WarningOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import {
  grievanceApi, type GrievanceActionType,
  type GrievanceListItem, type GrievanceSeverity, type GrievanceStatus,
} from '@/features/hr/grievanceApi'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'
import { usePlantId } from '@/shared/hooks/usePlantId'
import { useHrMobilePolish, type ThresholdResult } from '@/shared/styles/hrMobilePolish'
import RaiseGrievanceModal from './RaiseGrievanceModal'

dayjs.extend(relativeTime)
const { Text, Title, Paragraph } = Typography

const SEVERITY_COLOR: Record<GrievanceSeverity, string> = {
  LOW: 'default', MEDIUM: 'blue', HIGH: 'orange', CRITICAL: 'red',
}
const STATUS_COLOR: Record<GrievanceStatus, string> = {
  OPEN: 'default', IN_PROGRESS: 'processing', RESOLVED: 'green',
  REJECTED: 'red', CLOSED: 'green',
}

function overdueClass(g: GrievanceListItem): ThresholdResult {
  if (g.status === 'CLOSED' || g.status === 'RESOLVED' || g.status === 'REJECTED') return 'neutral'
  if (g.is_overdue) return 'red'
  // Within 25% of SLA window → amber. Doc §9 has no exact pct; "approaching
  // due" is a common HR practice.
  if (g.due_at) {
    const dueIn = dayjs(g.due_at).diff(dayjs(), 'hour')
    if (dueIn < 4) return 'amber'
  }
  return 'green'
}

export default function GrievanceInboxPage() {
  useHrMobilePolish()
  const { plantId } = usePlantId()
  const canUpdate = usePermission('grievance.update')
  const [statusFilter, setStatusFilter] = useState<'OPEN_ALL' | 'OVERDUE' | 'CLOSED' | 'ALL'>('OPEN_ALL')
  const [severityFilter, setSeverityFilter] = useState<GrievanceSeverity | 'ALL'>('ALL')
  const [raiseOpen, setRaiseOpen] = useState(false)
  const [detailId, setDetailId] = useState<string | null>(null)

  const qc = useQueryClient()

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['grievance-list', plantId, statusFilter, severityFilter],
    queryFn: () => grievanceApi.list({
      plant_id: plantId,
      status_in: statusFilter === 'OPEN_ALL' ? ['OPEN', 'IN_PROGRESS']
              : statusFilter === 'OVERDUE' ? ['OPEN', 'IN_PROGRESS']
              : statusFilter === 'CLOSED' ? ['RESOLVED', 'REJECTED', 'CLOSED']
              : undefined,
      severity: severityFilter === 'ALL' ? undefined : severityFilter,
      overdue_only: statusFilter === 'OVERDUE',
      limit: 300,
    }),
    enabled: !!plantId,
  })

  const stats = useMemo(() => {
    const total = rows.length
    const overdue = rows.filter((r) => r.is_overdue).length
    const crit = rows.filter((r) => r.severity === 'CRITICAL').length
    const repeat = rows.filter((r) => r.raised_by_name?.toLowerCase()?.includes('anonymous')).length
    void repeat  // server is_repeat_of_id is on the detail; list shows count separately later
    const avgAge = total > 0
      ? rows.reduce((s, r) => s + r.age_hours, 0) / total
      : 0
    return { total, overdue, crit, avgAge }
  }, [rows])

  const refresh = () => qc.invalidateQueries({ queryKey: ['grievance-list'] })

  return (
    <div className="hr-mobile-root">
      <PageHeader
        title="Grievances"
        subtitle="Doc §9 — log, triage, close. SLA timer per row."
        extra={
          <Space>
            <Button icon={<PlusOutlined />} onClick={() => setRaiseOpen(true)}>
              Raise on behalf
            </Button>
          </Space>
        }
      />

      {/* Threshold tiles */}
      <div style={{
        display: 'grid', gap: 8, marginBottom: 12,
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
      }}>
        <KPITile variant="neutral" value={stats.total} label="Open + closed (filter)" />
        <KPITile variant={stats.overdue > 0 ? 'red' : 'green'}
                 value={stats.overdue} label="Overdue" />
        <KPITile variant={stats.crit > 0 ? 'red' : 'neutral'}
                 value={stats.crit} label="Critical" />
        <KPITile variant="neutral"
                 value={stats.avgAge.toFixed(1)} label="Avg age (h)" />
      </div>

      {/* Filter chips */}
      <div className="hr-filter-chips" style={{ marginBottom: 8 }}>
        {(['OPEN_ALL', 'OVERDUE', 'CLOSED', 'ALL'] as const).map((s) => (
          <div key={s} role="button" tabIndex={0}
               className={`hr-filter-chip${statusFilter === s ? ' hr-filter-chip--active' : ''}`}
               onClick={() => setStatusFilter(s)}>
            {s === 'OPEN_ALL' ? 'Open' : s === 'OVERDUE' ? 'Overdue' : s === 'CLOSED' ? 'Closed' : 'All'}
          </div>
        ))}
        <Select
          size="small" value={severityFilter} onChange={setSeverityFilter}
          style={{ minWidth: 130 }}
          options={[
            { value: 'ALL', label: 'All severity' },
            { value: 'CRITICAL', label: 'Critical' },
            { value: 'HIGH', label: 'High' },
            { value: 'MEDIUM', label: 'Medium' },
            { value: 'LOW', label: 'Low' },
          ]}
        />
      </div>

      {isLoading ? (
        <Empty description="Loading…" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : rows.length === 0 ? (
        <Empty description="No grievances in this filter."
               image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <div style={{
          display: 'grid', gap: 8,
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        }}>
          {rows.map((r) => (
            <GrievanceCard key={r.id} row={r} onOpen={() => setDetailId(r.id)} />
          ))}
        </div>
      )}

      <RaiseGrievanceModal
        open={raiseOpen}
        onClose={() => setRaiseOpen(false)}
        onRaised={refresh}
      />

      {detailId && (
        <GrievanceDetailDrawer
          id={detailId}
          open={!!detailId}
          canUpdate={canUpdate}
          onClose={() => setDetailId(null)}
          onActioned={refresh}
        />
      )}
    </div>
  )
}

// ─── Sub-components ────────────────────────────────────────────────

function KPITile({ variant, value, label }: {
  variant: ThresholdResult; value: number | string; label: string
}) {
  return (
    <div className={`threshold-tile threshold-tile--${variant}`}>
      <div className="threshold-tile__value">{value}</div>
      <div className="threshold-tile__label">{label}</div>
    </div>
  )
}

function GrievanceCard({ row, onOpen }: {
  row: GrievanceListItem; onOpen: () => void
}) {
  const variant = overdueClass(row)
  const overdueBy = row.due_at ? dayjs(row.due_at).fromNow() : null
  return (
    <div className="hr-roster-card"
         style={{
           flexDirection: 'column', alignItems: 'stretch', cursor: 'pointer',
           borderLeft: variant === 'red' ? '4px solid #cf1322'
                     : variant === 'amber' ? '4px solid #d48806'
                     : variant === 'green' ? '4px solid #389e0d'
                     : undefined,
         }}
         onClick={onOpen}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <Text strong style={{ fontSize: 13, fontFamily: 'monospace' }}>
          {row.grievance_no}
        </Text>
        <Tag color={SEVERITY_COLOR[row.severity]} style={{ margin: 0 }}>
          {row.severity}
        </Tag>
        <Tag color={STATUS_COLOR[row.status]} style={{ margin: 0 }}>
          {row.status}
        </Tag>
        {row.is_overdue && (
          <Tag color="red" icon={<WarningOutlined />} style={{ margin: 0 }}>
            Overdue
          </Tag>
        )}
      </div>
      <div style={{ marginTop: 6, fontSize: 13 }}>
        <Text strong>{row.category_name ?? '—'}</Text>
        {row.department_name && (
          <Text type="secondary"> · {row.department_name}</Text>
        )}
      </div>
      <div style={{ marginTop: 4, fontSize: 12, color: '#8c8c8c' }}>
        <Text>{row.is_anonymous ? '🕶 Anonymous' : (row.raised_by_name ?? '—')}</Text>
        <Text type="secondary"> · raised {dayjs(row.raised_at).fromNow()}</Text>
        {row.due_at && row.status !== 'CLOSED' && (
          <Text type="secondary"> · due {overdueBy}</Text>
        )}
      </div>
      {row.current_owner_name && (
        <div style={{ marginTop: 4, fontSize: 11 }}>
          <Tag>Owner: {row.current_owner_name}</Tag>
        </div>
      )}
    </div>
  )
}

function GrievanceDetailDrawer({ id, open, canUpdate, onClose, onActioned }: {
  id: string; open: boolean; canUpdate: boolean
  onClose: () => void; onActioned: () => void
}) {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { data: g, isLoading } = useQuery({
    queryKey: ['grievance', id],
    queryFn: () => grievanceApi.get(id),
    enabled: open,
  })
  const [actionModal, setActionModal] = useState<GrievanceActionType | null>(null)

  const actionMut = useMutation({
    mutationFn: (body: Parameters<typeof grievanceApi.postAction>[1]) =>
      grievanceApi.postAction(id, body),
    onSuccess: () => {
      message.success('Action recorded.')
      qc.invalidateQueries({ queryKey: ['grievance', id] })
      onActioned()
      setActionModal(null)
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => {
      message.error(e?.response?.data?.detail ?? 'Action failed')
    },
  })

  const open_ = g && (g.status === 'OPEN' || g.status === 'IN_PROGRESS')
  const finalised = g && (g.status === 'RESOLVED' || g.status === 'REJECTED')

  return (
    <Drawer
      title={g ? g.grievance_no : 'Loading…'}
      open={open} onClose={onClose}
      width={Math.min(560, window.innerWidth - 24)}
      placement="right"
    >
      {isLoading || !g ? (
        <Empty description="Loading…" />
      ) : (
        <>
          {g.is_overdue && (
            <Alert type="error" showIcon
                   message={`Overdue — due was ${dayjs(g.due_at).fromNow()}`}
                   style={{ marginBottom: 12 }} />
          )}
          <Space wrap style={{ marginBottom: 12 }}>
            <Tag color={SEVERITY_COLOR[g.severity]}>{g.severity}</Tag>
            <Tag color={STATUS_COLOR[g.status]}>{g.status}</Tag>
            {g.is_repeat_of_id && <Tag color="purple">REPEAT</Tag>}
            {g.is_anonymous && <Tag color="default">ANONYMOUS</Tag>}
          </Space>

          <Title level={5} style={{ marginTop: 0 }}>{g.category_name}</Title>
          <Paragraph>{g.description}</Paragraph>

          <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 12 }}>
            Raised by: <Text strong>{g.raised_by_name ?? '—'}</Text>
            {g.raised_by_emp_code && <> ({g.raised_by_emp_code})</>}
            <br />
            Department: {g.department_name ?? '—'}
            {g.about_supervisor_name && <> · About: {g.about_supervisor_name}</>}
            <br />
            Due: {g.due_at ? dayjs(g.due_at).format('DD MMM HH:mm') : '—'}
            {g.closed_at && (
              <> · Closed {dayjs(g.closed_at).fromNow()} ·
                  TAT {g.closure_tat_hours?.toFixed(1) ?? '—'}h</>
            )}
          </div>

          {canUpdate && (open_ || finalised) && (
            <Space wrap style={{ marginBottom: 16 }}>
              {open_ && (
                <>
                  <Button icon={<UserSwitchOutlined />}
                          onClick={() => setActionModal('ASSIGNED')}>Assign</Button>
                  <Button icon={<SendOutlined />}
                          onClick={() => setActionModal('ESCALATED')}>Escalate</Button>
                  <Button icon={<MessageOutlined />}
                          onClick={() => setActionModal('REPLIED')}>Reply</Button>
                  <Button type="primary" icon={<CheckCircleOutlined />}
                          onClick={() => setActionModal('RESOLVED')}>Resolve</Button>
                  <Button danger icon={<StopOutlined />}
                          onClick={() => setActionModal('REJECTED')}>Reject</Button>
                </>
              )}
              {finalised && (
                <>
                  <Button type="primary" icon={<CheckCircleOutlined />}
                          onClick={() => setActionModal('CLOSED')}>Close</Button>
                  <Button icon={<HistoryOutlined />}
                          onClick={() => setActionModal('REOPENED')}>Reopen</Button>
                </>
              )}
            </Space>
          )}

          <Title level={5}>Action history</Title>
          <List
            dataSource={g.actions}
            size="small"
            renderItem={(a) => (
              <List.Item>
                <List.Item.Meta
                  title={
                    <Space size={6}>
                      <Tag color="blue">{a.action_type}</Tag>
                      <Text style={{ fontSize: 12 }}>{a.action_by_name ?? '—'}</Text>
                      {a.to_user_name && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          → {a.to_user_name}
                        </Text>
                      )}
                    </Space>
                  }
                  description={
                    <>
                      <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                        {dayjs(a.action_at).format('DD MMM HH:mm')}
                      </div>
                      {a.notes && (
                        <Text style={{ fontSize: 12 }}>{a.notes}</Text>
                      )}
                    </>
                  }
                />
              </List.Item>
            )}
          />

          <ActionModal
            open={!!actionModal}
            action={actionModal}
            loading={actionMut.isPending}
            onClose={() => setActionModal(null)}
            onSubmit={(body) => actionMut.mutate(body)}
          />
        </>
      )}
    </Drawer>
  )
}

function ActionModal({ open, action, loading, onClose, onSubmit }: {
  open: boolean
  action: GrievanceActionType | null
  loading: boolean
  onClose: () => void
  onSubmit: (body: Parameters<typeof grievanceApi.postAction>[1]) => void
}) {
  const [notes, setNotes] = useState('')
  const [closureSummary, setClosureSummary] = useState('')
  const [rating, setRating] = useState<number | null>(null)
  const [toUserId, setToUserId] = useState<string | undefined>()

  // Reset on open
  useMemo(() => {
    if (open) {
      setNotes(''); setClosureSummary(''); setRating(null); setToUserId(undefined)
    }
  }, [open])

  if (!action) return null

  const needsClosure = action === 'RESOLVED' || action === 'REJECTED'
  const needsTo = action === 'ASSIGNED' || action === 'ESCALATED'
  const needsRating = action === 'CLOSED'

  return (
    <Modal
      open={open}
      title={`Action: ${action}`}
      onCancel={onClose}
      confirmLoading={loading}
      onOk={() => {
        onSubmit({
          action_type: action,
          notes: notes || null,
          to_user_id: needsTo ? (toUserId ?? null) : null,
          closure_summary: needsClosure ? closureSummary : null,
          satisfaction_score: needsRating ? rating : null,
        })
      }}
    >
      {needsTo && (
        <div style={{ marginBottom: 12 }}>
          <Text strong>Assign to (user ID)</Text>
          <Input value={toUserId ?? ''}
                 onChange={(e) => setToUserId(e.target.value)}
                 placeholder="User UUID" />
          <Text type="secondary" style={{ fontSize: 11 }}>
            (User picker comes from /hr/users; paste a UUID for now.)
          </Text>
        </div>
      )}
      {needsClosure && (
        <div style={{ marginBottom: 12 }}>
          <Text strong>Closure summary *</Text>
          <Input.TextArea rows={3} value={closureSummary}
                          onChange={(e) => setClosureSummary(e.target.value)}
                          placeholder="What was the resolution? (required)" />
        </div>
      )}
      {needsRating && (
        <div style={{ marginBottom: 12 }}>
          <Text strong>Worker satisfaction (1-5)</Text>
          <div><Rate value={rating ?? 0} onChange={setRating} /></div>
        </div>
      )}
      <div>
        <Text strong>{needsClosure || needsRating ? 'Additional notes' : 'Notes'}</Text>
        <Input.TextArea rows={3} value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Anything else to record" />
      </div>
    </Modal>
  )
}
